import i18n from '@/utils/i18n';
import moment from 'moment';
import React from 'react';
import { BORDER_WIDTH, SnakeBorderView } from '@/components/snake-border-view';
import { createStyles } from '@/contexts/theme-context';
import { FONTS } from '@/styles';
import { formatTime } from '@/utils/date';
import { gql } from '@apollo/client';
import { MultiStyleText } from '@/components/multi-style-text';
import {
  OrderEtaFieldsFragment,
  OrderEtaTrackingFieldsFragment,
} from '@/types';
import { OrderTrackingStatus } from '@/enums';
import { PixelRatio, View } from 'react-native';

export const ORDER_ETA_FRAGMENT = gql`
  fragment OrderETATrackingFields on order_tracking {
    lower_bound_delivery_datetime
    order_completed_delivery_datetime
    status
    upper_bound_delivery_datetime
    uuid
  }

  fragment OrderETAFields on orders {
    order_tracking {
      ...OrderETATrackingFields
    }
    restaurant_desired_delivery_time
    supplier_accepted_delivery_time
  }
`;

/*
 * Only display banner if order has tracking fields, and is not considered a
 * past order.
 * Past orders are defined as orders with an order_completed_delivery_datetime
 * set on the order_tracking record, or the expected fulfillment day has past
 * based on supplier or restaurant_desired_delivery_time
 */
export const canDisplayETABanner = (order?: OrderEtaFieldsFragment) => {
  const orderTracking = order?.order_tracking;

  if (!orderTracking) {
    return false;
  }

  const {
    lower_bound_delivery_datetime: lowerBound,
    upper_bound_delivery_datetime: upperBound,
    order_completed_delivery_datetime: completedTime,
  } = orderTracking;

  const expectedFulfillmentTime =
    order.supplier_accepted_delivery_time ||
    order.restaurant_desired_delivery_time;

  if (!lowerBound || !upperBound || completedTime) {
    return false;
  }

  const now = moment();

  const endOfExpectedDay = moment(expectedFulfillmentTime).endOf('day');
  return now.isBefore(endOfExpectedDay);
};

// Calculate component height to be used for setting recycler list view
// item heights
const LINE_HEIGHT = 14;
const TEXT_SIZE = PixelRatio.getFontScale() * LINE_HEIGHT;
const PADDING_VERTICAL = 4;
export const COMPONENT_HEIGHT =
  BORDER_WIDTH * 2 + PADDING_VERTICAL * 2 + TEXT_SIZE;

interface Props {
  orderTracking: OrderEtaTrackingFieldsFragment;
}

export const OrderETABanner: React.FC<Props> = ({ orderTracking }) => {
  const [styles] = useStyles();

  const {
    lower_bound_delivery_datetime: lowerBound,
    upper_bound_delivery_datetime: upperBound,
    status,
  } = orderTracking;

  const stopSkipped = status === OrderTrackingStatus.SKIPPED;

  return (
    <SnakeBorderView>
      <View style={styles.textWrapper}>
        <MultiStyleText
          defaultStyle={styles.estimateText}
          emphasisStyle={
            stopSkipped ? styles.skippedText : styles.estimateRange
          }
        >
          {i18n.t('estimated_arrival', {
            timeRange: stopSkipped
              ? i18n.t('not_available')
              : `${formatTime(lowerBound)} - ${formatTime(upperBound)}`,
          })}
        </MultiStyleText>
      </View>
    </SnakeBorderView>
  );
};

const useStyles = createStyles((theme) => ({
  estimateRange: {
    color: theme.sk.color.text.success,
  },
  estimateText: {
    ...FONTS.primarySemiBold,
    color: theme.colors.primary,
    fontSize: 12,
    lineHeight: LINE_HEIGHT,
    textTransform: 'uppercase',
  },
  skippedText: {
    color: theme.sk.color.text.error,
  },
  textWrapper: {
    paddingHorizontal: 16,
    paddingVertical: PADDING_VERTICAL,
  },
}));
